/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { Button } from '@strata/react-buttons';
import { Checkbox } from '@strata/react-checkbox-list';
import { FormContainer } from '@strata/react-form-container';
import { GridWithSearch, PrimaryData, ProductCell } from '@strata/react-grid';
import { TextInputFormik } from '@strata/react-inputs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { AddListItemsDialog } from 'pages/listManagement/dialogs/AddListItemsDialog';
import { DeleteListItemsDialog } from 'pages/listManagement/dialogs/DeleteListItemsDialog';
import { PrescriberBulkDeleteDialog } from 'pages/listManagement/dialogs/PrescriberBulkDeleteDialog';
import { ProductsBulkUploadDialog } from 'pages/listManagement/dialogs/ProductsBulkUploadDialog';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import {
    areListItemsEqual,
    getListItemName,
    ListItemColumnNames,
    ListSchema,
    ListSchemaApiURLPrefix,
    ListSchemaAppRoutePrefix,
    ListType,
    ListTypeItemKeyMap,
    ListTypeNameMap,
    pluralizeListTypeName,
} from 'services/ListManagementHelpers';

const columns = ({ listSchema, listType, formik }) => [
    {
        header: () => (
            <Checkbox
                name="selected"
                inline
                className="margin__top--x-small"
                checked={formik.values.selected?.length === formik.values.listItems?.length}
                onChange={(checked) =>
                    formik.setFieldValue('selected', checked ? [...formik.values.listItems] : [])
                }
            />
        ),
        accessorKey: 'checkbox',
        size: 48,
        enableSorting: false,
        cell: ({ row }) => (
            <Checkbox
                name="selected"
                inline
                checked={
                    formik.values.selected.findIndex((item) =>
                        areListItemsEqual(item, row.original, listType),
                    ) !== -1
                }
                onChange={(checked) =>
                    formik.setFieldValue(
                        'selected',
                        checked
                            ? [...formik.values.selected, row.original]
                            : formik.values.selected.filter(
                                  (item) => !areListItemsEqual(item, row.original, listType),
                              ),
                    )
                }
            />
        ),
    },
    ...ListTypeItemKeyMap[listType]
        .filter(
            (key) =>
                listSchema !== ListSchema.finance ||
                listType !== ListType.product ||
                key !== ListTypeItemKeyMap[ListType.product][2],
        )
        .map((key, i) => ({
            header: ListItemColumnNames[listType][i],
            accessorKey: key,
            size: 1000 / ListTypeItemKeyMap[listType].length,
            cell: ({ getValue, row }) => {
                if (listType === ListType.product) {
                    // Product Name With Brand Status Tag
                    if (key === ListTypeItemKeyMap[ListType.product][0]) {
                        return (
                            <ProductCell
                                product={getValue()}
                                isBrand={row.original.is_brand}
                                primary
                            />
                        );
                    }
                    // Product List Offer Rate
                    if (key === ListTypeItemKeyMap[ListType.product][2]) {
                        return (
                            <PrimaryData
                                data={
                                    <ServicingRate
                                        servicingRate={getValue() ?? undefined}
                                        referentPriceType={row.original.referent_price_type}
                                    />
                                }
                            />
                        );
                    }
                }
                return <PrimaryData data={getValue()} />;
            },
        })),
    {
        header: 'Actions',
        accessorKey: 'actions',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.SECONDARY}
                    title="Delete"
                    onClick={() => formik.setFieldValue('itemsToDelete', [row.original])}
                />
            </div>
        ),
    },
];

export const ListDetails = ({ listSchema }) => {
    const { listType, listId } = useParams();
    const navigate = useNavigate();

    const [isAddListItemsDialogOpen, setIsAddListItemsDialogOpen] = useState(false);
    const [isPrescriberBulkDeleteDialogOpen, setIsPrescriberBulkDeleteDialogOpen] = useState(false);
    const [isProductsBulkUploadDialogOpen, setIsProductsBulkUploadDialogOpen] = useState(false);

    const goHome = () => navigate(`${ListSchemaAppRoutePrefix[listSchema]}/${listType}`);

    const isCreate = listId === CREATE_ROUTE;

    const list = useGet(
        !isCreate ? `${ListSchemaApiURLPrefix[listSchema]}/${listType}/${listId}` : null,
    );

    const suppliers = useGet('/reference/suppliers/parents');
    const supplierMap = Object.fromEntries(
        (suppliers ?? []).map((supplier) => [supplier.supplier_npi, supplier.organization_name]),
    );

    const listItemName =
        listType !== ListType.product
            ? pluralizeListTypeName(getListItemName(listType))
            : getListItemName(listType);

    return (
        <Formik
            initialValues={{
                listName: list?.list_name,
                listDescription: list?.list_description,
                listItems: list?.list_items?.map((item) => {
                    if (listType === ListType.supplier) {
                        return {
                            ...item,
                            [ListTypeItemKeyMap[listType][0]]:
                                supplierMap?.[item[ListTypeItemKeyMap[listType][1]]],
                        };
                    }
                    return item;
                }),
                selected: [],
                itemsToDelete: [],
                addedItems: [],
                deletedItems: [],
            }}
            enableReinitialize
            validationSchema={Yup.object({
                listName: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .max(100, validationStrings.maxCharacter[100]),
                listDescription: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .max(100, validationStrings.maxCharacter[100]),
                listItems: Yup.array().required().min(1),
            })}
            onSubmit={async (values) => {
                try {
                    if (isCreate) {
                        await post(`${ListSchemaApiURLPrefix[listSchema]}/${listType}`, {
                            list_name: values.listName,
                            list_description: values.listDescription,
                            list_items: values.listItems,
                        });
                    } else {
                        await put(`${ListSchemaApiURLPrefix[listSchema]}/${listType}/${listId}`, {
                            list_name: values.listName,
                            list_description: values.listDescription,
                            added_items: values.addedItems,
                            deleted_items: values.deletedItems,
                        });
                    }

                    SuccessToast({
                        message: `${values.listName} ${
                            isCreate ? 'created' : 'updated'
                        } successfully.`,
                    });
                    goHome();
                } catch (err) {
                    handleError(`Error saving ${values.listName}.`, err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={!isCreate && !list}
                    formTitle={`${ListTypeNameMap[listType]} List Configuration`}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formLeftContent={
                        <>
                            <AddListItemsDialog
                                isOpen={isAddListItemsDialogOpen}
                                listSchema={listSchema}
                                listType={listType}
                                onClose={() => setIsAddListItemsDialogOpen(false)}
                            />
                            <DeleteListItemsDialog listType={listType} />
                            <TextInputFormik
                                label={`${ListTypeNameMap[listType]} List Name`}
                                id="listName"
                                helperText="Will be used as the display name throughout Lucent when referring to this list."
                                required
                            />
                            <TextInputFormik label="Description" id="listDescription" required />
                            <div style={{ flexGrow: 1, overflow: 'auto' }}>
                                <GridWithSearch
                                    data={formik.values.listItems}
                                    columns={columns({ listSchema, listType, formik })}
                                    sortProperties={[
                                        { id: ListTypeItemKeyMap[listType][0], desc: false },
                                    ]}
                                    rightHeaderContent={
                                        <div className="flex__gap--medium">
                                            {formik.values.listItems?.length > 0 && (
                                                <Button
                                                    label={`Delete ${listItemName}`}
                                                    intent={Button.Intent.DANGER}
                                                    disabled={formik.values.selected.length === 0}
                                                    onClick={() =>
                                                        formik.setFieldValue(
                                                            'itemsToDelete',
                                                            formik.values.selected,
                                                        )
                                                    }
                                                />
                                            )}
                                            {listType === ListType.prescriber && (
                                                <>
                                                    <Button
                                                        label="Bulk Delete Prescribers"
                                                        intent={Button.Intent.SECONDARY}
                                                        onClick={() =>
                                                            setIsPrescriberBulkDeleteDialogOpen(
                                                                true,
                                                            )
                                                        }
                                                    />
                                                    <PrescriberBulkDeleteDialog
                                                        isOpen={isPrescriberBulkDeleteDialogOpen}
                                                        onClose={() =>
                                                            setIsPrescriberBulkDeleteDialogOpen(
                                                                false,
                                                            )
                                                        }
                                                    />
                                                </>
                                            )}
                                            {listType === ListType.product && (
                                                <>
                                                    <Button
                                                        label="Bulk Upload Products"
                                                        intent={Button.Intent.PRIMARY}
                                                        onClick={() =>
                                                            setIsProductsBulkUploadDialogOpen(true)
                                                        }
                                                    />
                                                    <ProductsBulkUploadDialog
                                                        isOpen={isProductsBulkUploadDialogOpen}
                                                        listSchema={listSchema}
                                                        onClose={() =>
                                                            setIsProductsBulkUploadDialogOpen(false)
                                                        }
                                                    />
                                                </>
                                            )}
                                            <Button
                                                label={`Add ${listItemName}`}
                                                intent={Button.Intent.PRIMARY}
                                                onClick={() => setIsAddListItemsDialogOpen(true)}
                                            />
                                        </div>
                                    }
                                />
                            </div>
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ListDetails.propTypes = {
    listSchema: PropTypes.oneOf(Object.values(ListSchema)).isRequired,
};
